import { clsx, isMobile, isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import {
  Col,
  DownloadIcon,
  IconButton,
  InformationIcon,
  Modal,
  ModalSizes,
  PercentLoaderIcon,
  Row,
  TooltipPosition,
} from 'plataforma-fundacao-componentes';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { openModal } from 'store/modules/ModalActions';
import styles from './styles.module.scss';

interface DocCardProps extends React.HTMLAttributes<HTMLDivElement> {
  badge: string;
  description: string;
  disabled?: boolean;
  tooltipText?: string;
  loader?: number | null;
  onClick?: () => Promise<void>;
}

export const DocCard: React.FC<DocCardProps> = React.memo(
  ({
    badge,
    description,
    onClick,
    disabled = false,
    tooltipText,
    loader = null,
    ...rest
  }) => {
    const [isOperationStarted, setIsOperationStarted] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const dispatch = useDispatch();

    const handleClick = useCallback(async () => {
      if (onClick) {
        setIsOperationStarted(true);
        try {
          await onClick();
        } catch (error) {
          console.error('Erro ao executar a operação async', error);
        } finally {
          setIsOperationStarted(false);
        }
      }
    }, [onClick]);

    useEffect(() => {
      setShowLoader(isOperationStarted || !isNullOrUndefined(loader));
    }, [isOperationStarted, loader]);

    const isCompactView = isMobile() || window.innerWidth <= 768;
    const shouldDisabled = disabled || isOperationStarted;

    const infoModal = () => {
      const mKey = `alert${getUniqueKey()}`;

      dispatch(
        openModal(Modal, {
          preventMaskExit: true,
          size: ModalSizes.Small,
          modalKey: mKey,
          title: 'Informação',
          children: 'Vote em todas as pautas para liberar um voucher exclusivo',
        })
      );
    };

    return (
      <div
        className={clsx(styles['doc-card'], {
          [styles.mobile]: isCompactView && !disabled && !isOperationStarted,
        })}
        data-disabled={disabled}
        aria-disabled={disabled}
        tabIndex={shouldDisabled ? -1 : 0}
        style={{ position: 'relative' }}
        tooltip-position={TooltipPosition.Top}
        tooltip-text={
          !isCompactView && disabled
            ? tooltipText ??
              'Vote em todas as pautas para liberar um voucher exclusivo'
            : ''
        }
        {...(isCompactView && {
          role: 'button',
          onClick: shouldDisabled ? undefined : handleClick,
        })}
        {...rest}
      >
        <Row>
          <Row className={styles.top}>
            <Col className={styles.badge} cols={[6, 6, 10, 10, 10]} noPadding>
              <span>{badge}</span>
              {isCompactView && disabled && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    infoModal();
                  }}
                  icon={<InformationIcon />}
                />
              )}
            </Col>

            <Col
              cols={[6, 6, 2, 2, 2]}
              noPadding
              end
              style={{ color: '#3fa110', position: 'relative' }}
            >
              <div
                className={clsx(
                  styles['fade-transition'],
                  showLoader ? styles.hidden : styles.visible
                )}
              >
                <IconButton
                  disabled={shouldDisabled}
                  icon={<DownloadIcon />}
                  onClick={handleClick}
                />
              </div>
              <div
                className={clsx(
                  styles['fade-transition'],
                  showLoader ? styles.visible : styles.hidden
                )}
              >
                <PercentLoaderIcon
                  indefinido={!loader || loader <= 0}
                  value={loader ? Number(loader) : 0}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <p>{description}</p>
          </Row>
        </Row>
      </div>
    );
  }
);
